import { useMsal } from "@azure/msal-react";
import { CircularProgress } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	useDoctorEdit,
	LinkedBpDoctors,
	AvailableBpDoctors,
	DoctorForm
} from "../components/DoctorEdit";
import { AlertType, Clinic, DoctorUser } from "../models";
import { useAlertContext, useUserContext, useDoctorContext } from "../contexts";
import { apiAccessRequest } from "../constants";

import { PendingDoctorImage } from "../components/DoctorEdit/DoctorForm";
import { saveDoctorImage } from "../components/DoctorEdit/saveDoctorImage";
import { isValidLthCap, saveLthCap } from "../components/DoctorEdit/LocalTelehealthCapacity/saveLthCap";

export const DoctorEdit = () => {
	const [pendingImage, setPendingImage] = useState<PendingDoctorImage>(null);
	const { setRefreshDoctorImages } = useDoctorContext();
	const { account } = useUserContext();
	const { showAlert } = useAlertContext();
	const { instance } = useMsal();
	const location = useLocation();
	const navigate = useNavigate();
	const doctorUser = location.state?.user as DoctorUser | undefined;
	const clinic = location.state?.clinic as Clinic | undefined;
	const [sexError, setSexError] = useState("");

	useEffect(() => {
		setTimeout(() => {
			window.scrollTo({
				top: 0,
				left: 0,
				behavior: "smooth"
			});
		}, 500);
	}, [doctorUser]);

	const {
		state,
		fetchDoctorInstances,
		setField,
		linkBPDoctor,
		unlinkBPDoctor,
		toggleConsult,
		setUpdating,
		hasChanges
	} = useDoctorEdit(doctorUser);

	const hasUnsavedChanges = hasChanges || pendingImage !== null;

	useEffect(() => {
		setPendingImage(null);
	}, [state.doctor?.id]);

	const handleGenderChange = (value: string) => {
		setSexError("");
		setField(["doctor", "sex"], value);
	};

	const handleSave = useCallback(async () => {
		if (!state.doctor || state.isUpdating) return;

		if (state.doctor.sex.trim() === "" || state.doctor.sex.trim() === "None") {
			setSexError("Gender is required");
			showAlert({
				message: "Select a gender before saving",
				alertType: AlertType.error
			});
			return;
		}

		if (!isValidLthCap(state.doctor.lthDailyCap)) {
			showAlert({ message: "Enter a whole number of 0 or more for the daily LTH cap.", alertType: AlertType.error });
			return;
		}

		setUpdating(true);
		try {
			const consultsUrl = process.env.REACT_APP_CONSULTS_URL ?? "";
			const tokenResult = await instance
				.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				})
				.catch(() => null);

			const headers: any = { "Content-Type": "application/json" };
			if (tokenResult) {
				headers.Authorization = `Bearer ${tokenResult.accessToken}`;
			}

			const patchBody = {
				salutation: state.doctor.salutation,
				sex: state.doctor.sex,
				status: state.doctor.status,
				firstName: state.doctor.firstName,
				lastName: state.doctor.lastName
			};
			const doctorResult = await fetch(
				`${consultsUrl}doctor/${state.doctor.id}`,
				{
					method: "PATCH",
					headers,
					body: JSON.stringify(patchBody)
				}
			);

			if (!doctorResult.ok) throw new Error("Doctor update failed");

			const existing = state.originalDoctor?.bpDoctors?.map((b) => b.id) ?? [];
			const current = state.doctor.bpDoctors?.map((b) => b.id) ?? [];
			const removedBpDoctors = existing.filter((id) => !current.includes(id));
			const addedBpDoctors = current.filter((id) => !existing.includes(id));

			for (const removed of removedBpDoctors) {
				const result = await fetch(
					`${consultsUrl}doctor/${state.doctor.id}/doctorinstance/${removed}/Unlink`,
					{ method: "DELETE", headers }
				);
				if (!result.ok) throw new Error("Unlink failed");
			}

			for (const added of addedBpDoctors) {
				const result = await fetch(
					`${consultsUrl}doctor/${state.doctor.id}/doctorinstance/${added}/link`,
					{ method: "POST", headers }
				);
				if (!result.ok) {
					const json = await result.json().catch(() => null);
					if (json?.detail) {
						showAlert({
							message: json?.detail,
							alertType: AlertType.error
						});
						setTimeout(() => {
							window.scrollTo(0, 0);
						}, 500);
						return;
					}
					throw new Error("link failed");
				}
			}

			const availabilityBody = (state.doctor.bpDoctors || []).map((d) => ({
				clinicId: d.clinicId,
				takingFaceToFace: d.takingFaceToFace,
				takingTelehealth: d.takingTelehealth,
				takingLocalTelehealth: d.takingLocalTelehealth ?? false,
				takingAppointments: d.takingAppointments ?? false
			}));
			const availabilityResult = await fetch(
				`${consultsUrl}doctor/${state.doctor.id}/availability`,
				{
					method: "PATCH",
					headers,
					body: JSON.stringify(availabilityBody)
				}
			);

			if (!availabilityResult.ok) throw new Error("Availability update failed");
			await saveDoctorImage(state.doctor.id, pendingImage, headers);
			if (pendingImage) setRefreshDoctorImages(true);
			await saveLthCap(
				state.doctor.id,
				state.doctor.lthDailyCap,
				state.originalDoctor?.lthDailyCap ?? null,
				headers
			);

			showAlert({
				message: "Changes have been saved",
				alertType: AlertType.success,
				timeout: true
			});
			navigate("/doctorAdmin", { state: { refreshRequired: true, clinic } });
		} catch (e) {
			console.error(e);
			showAlert({
				message: e instanceof Error ? e.message : "There was an error updating the doctor",
				alertType: AlertType.error
			});
			setTimeout(() => {
				window.scrollTo(0, 0);
			}, 500);
		} finally {
			setUpdating(false);
		}
	}, [
		state.doctor,
		state.isUpdating,
		pendingImage,
		setRefreshDoctorImages,
		state.originalDoctor,
		setUpdating,
		instance,
		account,
		navigate,
		showAlert,
		clinic
	]);

	const handleGoBack = () => {
		if (state.isUpdating) return;
		if (hasUnsavedChanges) {
			if (!window.confirm("You have unsaved changes. Exit without saving?"))
				return;
		}
		navigate("/doctorAdmin", { state: { refreshRequired: false, clinic } });
	};

	const handleImport = async () => {
		try {
			const consultsUrl = process.env.REACT_APP_CONSULTS_URL ?? "";
			const tokenResult = await instance
				.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				})
				.catch(() => null);
			const headers: any = { "Content-Type": "application/json" };
			if (tokenResult) {
				headers.Authorization = `Bearer ${tokenResult.accessToken}`;
			}
			await fetch(`${consultsUrl}Doctor/sync`, { method: "POST", headers });
			window.location.reload();
		} catch (e) {
			console.error(e);
		}
	};

	useEffect(() => {
		const handler = (e: BeforeUnloadEvent) => {
			if (hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = "";
				return "";
			}
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [hasUnsavedChanges]);

	if (!doctorUser) return null;

	if (!state.doctor) {
		return (
			<div className="flex grow h-full justify-center items-center">
				<CircularProgress size="3rem" />
			</div>
		);
	}

	return (
		<>
			<div
				className="flex mb-4"
				style={{ cursor: "pointer" }}
				onClick={() => handleGoBack()}
			>
				<img
					src="images/svg/arrow-left-black.svg"
					className="mr-2"
					height="24"
					width="24"
					alt="back to Doctors Admin"
				/>
				<div>Doctors Admin</div>
			</div>
			<div className="flex justify-between">
				<div className="flex flex-col">
					<div className="ch-l-text">Edit Doctor</div>
					<div>{`${state.doctor?.firstName ?? ""} ${state.doctor?.lastName ?? ""}`}</div>
				</div>
				<div className="flex">
					<div className="flex gap-4 mt-6">
						<button
							className="ch-button ch-button-primary"
							onClick={handleGoBack}
							disabled={state.isUpdating}
						>
							Cancel
						</button>
						<button
							className="ch-button ch-button-secondary"
							onClick={handleSave}
							disabled={state.isUpdating}
						>
							Save
						</button>
					</div>
				</div>
			</div>

			<div className="flex flex-col gap-4">
				<DoctorForm
					pendingImage={pendingImage}
					onImageChange={setPendingImage}
					isSaving={state.isUpdating}
					doctor={state.doctor}
					sexError={sexError}
					onFieldChange={(path, value) => setField(path, value)}
					onGenderChange={handleGenderChange}
				/>

				<LinkedBpDoctors
					doctor={state.doctor}
					onUnlink={(id) => unlinkBPDoctor(id)}
					onToggle={(t, id) => toggleConsult(id, t)}
					errors={state.errors}
				/>
				<div>
					<div className="w-100 my-8"></div>
				</div>
				<AvailableBpDoctors
					bpDoctors={state.doctor.bpDoctors || []}
					fetchDoctorInstances={fetchDoctorInstances}
					doctorId={state.doctor?.id}
					onLink={(bp) => linkBPDoctor(bp)}
					onImport={handleImport}
				/>
			</div>
		</>
	);
};
